import React, { useEffect, useRef, useState } from "react";
import "./YearIndicatorSvg.scss";
import { sceneCount } from "../../../App";

interface TouchState {
    touching: boolean;
    percentage: number;
    height: number;
    top: number;
}

interface IProps {
    topEnabled: boolean;
    bottomEnabled: boolean;
    showBall: boolean;
    sliderValue: number;
    setSliderValue: (value: number) => void;
}

export function YearIndicatorSvg(props: IProps) {
    const steps = sceneCount;
    const { sliderValue, setSliderValue } = props;
    const pathRef = useRef(null);
    const [touchState, setTouchState]  = useState<TouchState>({ touching: false, percentage: sliderValue, height: 0, top: 0});
    // @ts-ignore
    const isTouchDevice = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));
    const { touching } = touchState;

    const step = 1 + Math.round(sceneCount / 100 * sliderValue);

    const handleBallTouchDown = ({clientY, touches}: any) => {
        const path = pathRef.current as any as HTMLDivElement;
        const pathBox = path.getBoundingClientRect();
        const y = clientY ?? (touches[0] as any as Touch).clientY;
        setTouchState({
            touching: true,
            percentage: Math.max(0, Math.min(100, (y - pathBox.top) / pathBox.height * 100)),
            height: pathBox.height,
            top: pathBox.top,
        });
    };

    useEffect(() => {
        if (!touchState.touching) {
            const oneStep = 1 / steps;
            const desiredPercentage = Math.round((touchState.percentage / 100) / oneStep) * oneStep;
            setSliderValue(desiredPercentage * 100);
        }
    }, [touchState, setSliderValue, steps]);

    useEffect(() => {
        if (touching) {
            const handleBallTouchMove = ({clientY, touches}: any) => {
                const y = clientY ?? (touches[0] as any as Touch).clientY;
                setTouchState((prevState) => {
                    return {
                        ...prevState,
                        percentage: Math.max(0, Math.min(100, (y - prevState.top) / prevState.height * 100)),
                    }
                });
            };
            const handleBallTouchUp = () => {
                setTouchState((prevState) => {
                    return {
                        ...prevState,
                        touching: false,
                    }
                });

            };
            if (isTouchDevice) {
                window.addEventListener('touchmove', handleBallTouchMove);
                window.addEventListener('touchend', handleBallTouchUp);

                return () => {
                    window.removeEventListener('touchmove', handleBallTouchMove);
                    window.removeEventListener('touchend', handleBallTouchUp);
                }
            } else {
                window.addEventListener('mousemove', handleBallTouchMove);
                window.addEventListener('mouseup', handleBallTouchUp);

                return () => {
                    window.removeEventListener('mousemove', handleBallTouchMove);
                    window.removeEventListener('mouseup', handleBallTouchUp);
                }
            }
        }
    }, [isTouchDevice, touching]);

    const classes = [
        'year-indicator-svg-wrapper',
        (props.topEnabled ? 'top-enabled' : ''),
        (props.bottomEnabled ? 'bottom-enabled' : ''),
        (props.showBall ? 'show-ball' : ''),
        (touching ? 'is-touching' : '')
    ];

    const ballStyle = (touching ? {
        offsetDistance: `${touchState.percentage}%`
    } : {});
    const fakeBallStyle = {
        top: `calc(${touching ? touchState.percentage : sliderValue}% * 0.92)`,
    }

    return (
        <>
            <div className={classes.join(' ')} data-step={step}>
                <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg"
                     x="0px" y="0px"
                     viewBox="0 0 600 600">
                    <style type="text/css">

                    </style>
                    <g className="st0">
                        <rect className="st1" width="600" height="600"/>
                    </g>
                    <g id="Simplified_element" transform="translate(-3627.163 -84)">
                        <g id="RIGHT_PART" transform="translate(4061.777 175.469)">
                            <g id="design">
                                <path id="veld" className="st2" d="M74.2,391.8c-9.1,0-16.4-7.4-16.4-16.5c0-3.7,1.3-7.3,3.5-10.2c73.6-93.3,73.6-225,0-318.3
				c-5.6-7.1-4.4-17.5,2.7-23.1c7.1-5.6,17.5-4.4,23.1,2.7l0,0c83.1,105.3,83.1,253.8,0,359.1C84,389.5,79.3,391.8,74.2,391.8"/>
                                <path id="Path_37" className="st3" d="M77.1,375.6c0,1.5-1.2,2.8-2.8,2.8c-1.5,0-2.8-1.2-2.8-2.8c0-1.5,1.2-2.8,2.8-2.8
				C75.8,372.8,77.1,374,77.1,375.6L77.1,375.6 M90.5,349.9c-1.5,0-2.8,1.2-2.8,2.8c0,1.5,1.2,2.8,2.8,2.8c1.5,0,2.8-1.2,2.8-2.8
				C93.3,351.2,92,349.9,90.5,349.9C90.5,349.9,90.5,349.9,90.5,349.9 M104.3,325.5c-1.5,0-2.8,1.2-2.8,2.8c0,1.5,1.2,2.8,2.8,2.8
				s2.8-1.2,2.8-2.8S105.9,325.5,104.3,325.5C104.3,325.5,104.3,325.5,104.3,325.5 M115.6,299.7c-1.5,0-2.8,1.2-2.8,2.8
				c0,1.5,1.2,2.8,2.8,2.8s2.8-1.2,2.8-2.8l0,0C118.4,301,117.1,299.7,115.6,299.7 M124.1,273c-1.5,0-2.8,1.2-2.8,2.8
				c0,1.5,1.2,2.8,2.8,2.8c1.5,0,2.8-1.2,2.8-2.8c0,0,0,0,0,0C126.9,274.2,125.6,273,124.1,273 M129.8,245.5c-1.5,0-2.8,1.2-2.8,2.8
				c0,1.5,1.2,2.8,2.8,2.8c1.5,0,2.8-1.2,2.8-2.8l0,0C132.6,246.7,131.3,245.5,129.8,245.5 M132.7,217.6c-1.5,0-2.8,1.2-2.8,2.8
				c0,1.5,1.2,2.8,2.8,2.8c1.5,0,2.8-1.2,2.8-2.8l0,0C135.4,218.8,134.2,217.6,132.7,217.6C132.7,217.6,132.7,217.6,132.7,217.6
				 M129.9,192.2c0,1.5,1.2,2.8,2.8,2.8s2.8-1.2,2.8-2.8c0-1.5-1.2-2.8-2.8-2.8c0,0,0,0,0,0C131.1,189.5,129.9,190.7,129.9,192.2
				 M129.8,167.1c1.5,0,2.8-1.2,2.8-2.8c0-1.5-1.2-2.8-2.8-2.8c-1.5,0-2.8,1.2-2.8,2.8l0,0C127,165.8,128.3,167.1,129.8,167.1
				C129.8,167.1,129.8,167.1,129.8,167.1 M124.1,134c-1.5,0-2.8,1.2-2.8,2.8c0,1.5,1.2,2.8,2.8,2.8c1.5,0,2.8-1.2,2.8-2.8
				C126.9,135.2,125.6,134,124.1,134C124.1,134,124.1,134,124.1,134 M115.6,107.2c-1.5,0-2.8,1.2-2.8,2.8c0,1.5,1.2,2.8,2.8,2.8
				s2.8-1.2,2.8-2.8l0,0C118.4,108.5,117.1,107.2,115.6,107.2 M104.4,81.5c-1.5,0-2.8,1.2-2.8,2.8s1.2,2.8,2.8,2.8s2.8-1.2,2.8-2.8
				c0,0,0,0,0,0C107.1,82.7,105.9,81.5,104.4,81.5C104.4,81.5,104.4,81.5,104.4,81.5 M90.5,57c-1.5,0-2.8,1.2-2.8,2.8
				c0,1.5,1.2,2.8,2.8,2.8c1.5,0,2.8-1.2,2.8-2.8C93.3,58.3,92.1,57,90.5,57C90.5,57,90.5,57,90.5,57 M74.3,34.2
				c-1.5,0-2.8,1.2-2.8,2.8s1.2,2.8,2.8,2.8c1.5,0,2.8-1.2,2.8-2.8l0,0C77.1,35.4,75.9,34.2,74.3,34.2"/>
                            </g>
                            <path id="bol" className="st3" d="M142.2,249.3c0-6.7-5.4-12.1-12.1-12.1c-6.7,0-12.1,5.4-12.1,12.1s5.4,12.1,12.1,12.1l0,0
			C136.8,261.4,142.2,256,142.2,249.3"
                                  style={ballStyle}
                            />
                            <path id="Path_38" className="st4"
                                  d="M74.5,36.7c38,48.3,58.7,107.9,58.8,169.3c-0.1,61.4-20.8,121.1-58.8,169.3"/>
                        </g>
                        <g id="artwork_line" transform="translate(3630 92)">
                            <path id="LIne" className="st3" d="M252.5,562.8c-1.5-0.2-2.6-1.6-2.4-3.1c0.2-1.5,1.6-2.6,3.1-2.4c1.5,0.2,2.6,1.6,2.4,3.1
			c-0.2,1.4-1.3,2.4-2.7,2.4C252.7,562.8,252.6,562.8,252.5,562.8 M324.8,560.3c-0.2-1.5,0.9-2.9,2.4-3.1c1.5-0.2,2.9,0.9,3.1,2.4
			c0.2,1.5-0.9,2.9-2.4,3.1c-0.1,0-0.2,0-0.4,0C326.2,562.7,325,561.7,324.8,560.3 M233.9,559.8c-1.5-0.3-2.5-1.7-2.2-3.2
			c0.3-1.5,1.7-2.5,3.2-2.2l0,0h0c1.5,0.3,2.4,1.7,2.1,3.2c-0.3,1.3-1.4,2.2-2.7,2.2C234.2,559.9,234,559.8,233.9,559.8
			 M343.3,557.5c-0.3-1.5,0.7-2.9,2.2-3.2c1.5-0.3,2.9,0.7,3.2,2.2c0.3,1.5-0.7,2.9-2.2,3.2c-0.2,0-0.3,0-0.5,0
			C344.7,559.7,343.6,558.8,343.3,557.5 M215.4,555.6L215.4,555.6c-1.5-0.4-2.3-1.9-2-3.3c0.4-1.5,1.9-2.3,3.3-2
			c1.5,0.4,2.3,1.9,2,3.3c-0.3,1.2-1.4,2-2.6,2C215.9,555.7,215.7,555.7,215.4,555.6 M361.6,553.5c-0.4-1.5,0.5-3,2-3.3
			c1.5-0.4,3,0.5,3.3,2c0.4,1.5-0.5,3-1.9,3.3c-0.2,0.1-0.5,0.1-0.7,0.1C363,555.5,361.9,554.7,361.6,553.5 M197.3,550.2
			c-1.4-0.5-2.2-2-1.7-3.5s2-2.2,3.5-1.7c1.4,0.5,2.2,2,1.7,3.5c-0.4,1.1-1.4,1.9-2.6,1.9C197.9,550.4,197.6,550.3,197.3,550.2
			 M379.6,548.3c-0.5-1.4,0.3-3,1.7-3.5c1.4-0.5,3,0.3,3.5,1.7c0.5,1.4-0.3,3-1.7,3.5c-0.3,0.1-0.6,0.1-0.9,0.1
			C381,550.2,380,549.4,379.6,548.3 M179.6,543.6L179.6,543.6c-1.4-0.6-2.1-2.2-1.5-3.6s2.2-2.1,3.6-1.5c1.4,0.6,2.1,2.2,1.5,3.6
			c-0.4,1-1.4,1.7-2.5,1.7C180.3,543.8,179.9,543.8,179.6,543.6 M397.2,541.9c-0.6-1.4,0.1-3,1.5-3.6c1.4-0.6,3,0.1,3.6,1.5
			s-0.1,3-1.5,3.6c-0.3,0.1-0.7,0.2-1.1,0.2C398.6,543.6,397.6,542.9,397.2,541.9 M162.4,535.8L162.4,535.8c-1.4-0.7-2-2.3-1.3-3.7
			s2.3-2,3.7-1.3c1.4,0.7,2,2.3,1.3,3.7c-0.5,1-1.4,1.6-2.5,1.6C163.2,536.1,162.8,536,162.4,535.8 M414.3,534.3
			c-0.7-1.4-0.1-3,1.2-3.7c1.4-0.7,3-0.1,3.7,1.2c0.7,1.4,0.1,3-1.2,3.7c-0.4,0.2-0.8,0.3-1.2,0.3
			C415.7,535.8,414.7,535.2,414.3,534.3 M145.8,526.9c-1.3-0.8-1.8-2.4-1-3.8c0.8-1.3,2.4-1.8,3.8-1c1.3,0.8,1.8,2.4,1,3.8
			c-0.5,0.8-1.4,1.4-2.4,1.4C146.7,527.3,146.2,527.1,145.8,526.9 M430.8,525.6c-0.8-1.3-0.3-3,1-3.8c1.3-0.8,3-0.3,3.8,1
			c0.8,1.3,0.3,3-1,3.8c0,0,0,0,0,0c-0.4,0.2-0.9,0.4-1.4,0.4C432.2,526.9,431.3,526.4,430.8,525.6 M129.8,516.8
			c-1.2-0.9-1.6-2.6-0.7-3.8c0.9-1.2,2.6-1.6,3.8-0.7s1.6,2.6,0.7,3.8c-0.5,0.7-1.4,1.2-2.3,1.2
			C130.8,517.2,130.3,517.1,129.8,516.8 M446.7,515.7c-0.9-1.2-0.6-3,0.7-3.8c1.2-0.9,3-0.6,3.8,0.7c0.9,1.2,0.6,3-0.7,3.8
			c-0.5,0.3-1,0.5-1.6,0.5C448.1,516.9,447.2,516.4,446.7,515.7 M114.6,505.5c-1.2-0.9-1.4-2.7-0.5-3.9c0.9-1.2,2.7-1.4,3.9-0.5
			c1.2,0.9,1.4,2.7,0.5,3.9c-0.5,0.7-1.3,1.1-2.2,1.1C115.7,506.1,115.1,505.9,114.6,505.5 M461.9,504.7c-0.9-1.2-0.7-2.9,0.4-3.9
			c1.2-0.9,2.9-0.7,3.9,0.4c0.9,1.2,0.8,2.9-0.4,3.8C464.5,506,462.8,505.8,461.9,504.7 M100.3,493.2c-1.1-1-1.2-2.8-0.2-3.9
			s2.8-1.2,3.9-0.2c1.1,1,1.2,2.8,0.2,3.9c0,0,0,0,0,0C103.1,494.2,101.4,494.3,100.3,493.2C100.3,493.2,100.3,493.2,100.3,493.2
			 M476.2,492.6c-1-1.1-1-2.8,0.1-3.9c1.1-1,2.8-1,3.9,0.1c1,1.1,1,2.8-0.1,3.9c0,0,0,0,0,0C478.9,493.7,477.2,493.7,476.2,492.6
			C476.2,492.6,476.2,492.6,476.2,492.6 M86.9,479.9L86.9,479.9c-1-1.1-1-2.8,0.1-3.9c1.1-1,2.8-1,3.9,0.1v0c1,1.2,0.8,2.9-0.3,3.9
			C89.5,480.9,87.9,480.9,86.9,479.9 M74.5,465.6c-1-1.2-0.8-2.9,0.4-3.9c1.2-1,2.9-0.8,3.9,0.4c1,1.2,0.8,2.9-0.4,3.9
			c-0.5,0.4-1.1,0.6-1.7,0.6C75.8,466.6,75,466.2,74.5,465.6 M63.2,450.4c-0.9-1.2-0.5-3,0.7-3.8c1.2-0.9,3-0.5,3.8,0.7
			c0.9,1.2,0.5,3-0.7,3.8c0,0,0,0,0,0C65.8,452,64.1,451.7,63.2,450.4 M53.1,434.5L53.1,434.5c-0.8-1.3-0.3-3,1-3.7
			c1.3-0.8,3-0.3,3.7,1c0.8,1.3,0.3,3-1,3.7c-0.4,0.2-0.9,0.4-1.4,0.4C54.4,435.8,53.5,435.3,53.1,434.5 M44.1,417.9L44.1,417.9
			c-0.7-1.4-0.1-3,1.2-3.7c1.4-0.7,3-0.1,3.7,1.2c0.7,1.4,0.1,3-1.2,3.7c-0.4,0.2-0.8,0.3-1.2,0.3C45.5,419.4,44.5,418.8,44.1,417.9
			 M36.3,400.7c-0.6-1.4,0.1-3,1.5-3.6c1.4-0.6,3,0.1,3.6,1.5c0.6,1.4-0.1,3-1.5,3.6c-0.3,0.1-0.7,0.2-1,0.2
			C37.7,402.4,36.7,401.7,36.3,400.7 M29.6,383c-0.5-1.4,0.3-3,1.7-3.5c1.4-0.5,3,0.3,3.5,1.7c0.5,1.4-0.3,3-1.7,3.5c0,0,0,0,0,0
			c-0.3,0.1-0.6,0.2-0.9,0.2C31,384.8,30,384.1,29.6,383 M24.2,364.9c-0.4-1.5,0.5-3,1.9-3.3s3,0.5,3.3,1.9c0.4,1.5-0.5,3-1.9,3.3
			c-0.2,0.1-0.5,0.1-0.7,0.1C25.6,367,24.5,366.1,24.2,364.9 M19.8,346.6c-0.3-1.5,0.7-2.9,2.1-3.2c1.5-0.3,2.9,0.7,3.2,2.1
			c0.3,1.5-0.7,2.9-2.1,3.2c-0.2,0-0.4,0.1-0.5,0.1C21.2,348.8,20.1,347.8,19.8,346.6 M16.6,328c-0.2-1.5,0.8-2.9,2.3-3.1
			c1.5-0.2,2.9,0.8,3.1,2.3c0.2,1.5-0.8,2.9-2.3,3.1c-0.1,0-0.3,0-0.4,0C17.9,330.3,16.8,329.3,16.6,328 M14.3,309.3
			c-0.1-1.5,1-2.8,2.5-3c1.5-0.1,2.8,1,3,2.5c0.1,1.5-1,2.8-2.5,3c-0.1,0-0.2,0-0.3,0C15.6,311.8,14.5,310.7,14.3,309.3 M13.1,290.4
			L13.1,290.4c-0.1-1.5,1.1-2.8,2.6-2.8c1.5-0.1,2.8,1.1,2.9,2.6c0.1,1.5-1.1,2.8-2.6,2.9c0,0,0,0,0,0h-0.1
			C14.4,293.1,13.1,291.9,13.1,290.4 M16.2,274.4c-1.5-0.1-2.5-1.4-2.4-2.9c0.1-1.5,1.4-2.5,2.9-2.4c1.5,0.1,2.5,1.4,2.4,2.9
			c-0.1,1.4-1.3,2.4-2.7,2.4C16.3,274.4,16.3,274.4,16.2,274.4 M17.9,255.7c-1.5-0.2-2.5-1.6-2.2-3.1c0.2-1.5,1.6-2.5,3.1-2.2
			c1.5,0.2,2.5,1.6,2.2,3.1c-0.2,1.3-1.4,2.3-2.7,2.3C18.1,255.8,18,255.8,17.9,255.7 M20.8,237.3c-1.5-0.3-2.4-1.7-2.1-3.2
			c0.3-1.5,1.7-2.4,3.2-2.1c1.5,0.3,2.4,1.7,2.1,3.2c-0.3,1.3-1.4,2.2-2.7,2.2C21.2,237.3,21,237.3,20.8,237.3 M25,219
			c-1.5-0.4-2.3-1.9-1.9-3.4c0.4-1.5,1.9-2.3,3.4-1.9c1.5,0.4,2.3,1.9,1.9,3.4c-0.3,1.2-1.4,2-2.6,2C25.5,219.1,25.2,219.1,25,219
			 M30.4,201.1c-1.4-0.5-2.2-2.1-1.7-3.5c0.5-1.4,2.1-2.2,3.5-1.7c1.4,0.5,2.2,2.1,1.7,3.5c-0.4,1.1-1.4,1.8-2.6,1.8
			C31,201.3,30.7,201.2,30.4,201.1 M37.1,183.6c-1.4-0.6-2.1-2.2-1.5-3.6c0.6-1.4,2.2-2.1,3.6-1.5c1.4,0.6,2.1,2.2,1.5,3.6
			c-0.4,1-1.4,1.7-2.5,1.7C37.8,183.8,37.5,183.8,37.1,183.6 M45,166.6c-1.3-0.7-1.9-2.4-1.2-3.7c0.7-1.3,2.4-1.9,3.7-1.2
			c1.3,0.7,1.9,2.4,1.2,3.7c-0.5,0.9-1.4,1.5-2.4,1.5C45.8,166.9,45.3,166.8,45,166.6 M53.9,150.2c-1.3-0.8-1.7-2.5-0.9-3.8
			c0.8-1.3,2.5-1.7,3.8-0.9c1.3,0.8,1.7,2.5,0.9,3.8c-0.5,0.8-1.4,1.3-2.4,1.3C54.9,150.6,54.4,150.4,53.9,150.2 M64,134.4
			c-1.2-0.9-1.5-2.6-0.7-3.8c0.9-1.2,2.6-1.5,3.8-0.7c1.2,0.9,1.5,2.6,0.7,3.8c-0.5,0.7-1.3,1.2-2.2,1.2
			C65.1,134.9,64.5,134.7,64,134.4 M75.2,119.4c-1.2-1-1.4-2.7-0.4-3.9s2.7-1.4,3.9-0.4s1.4,2.7,0.4,3.9c-0.5,0.6-1.3,1-2.1,1
			C76.3,120,75.7,119.8,75.2,119.4 M87.3,105.1c-1.1-1-1.2-2.8-0.1-3.9c1-1.1,2.8-1.2,3.9-0.1c1.1,1,1.2,2.8,0.1,3.9
			c-0.5,0.6-1.2,0.9-2,0.9C88.5,105.9,87.8,105.6,87.3,105.1 M100.4,91.7c-1-1.1-1-2.8,0.1-3.9s2.8-1,3.9,0.1c1,1.1,1,2.8-0.1,3.9
			c-0.5,0.5-1.2,0.7-1.9,0.7C101.6,92.6,100.9,92.3,100.4,91.7 M475.1,91.5c-1.1-1-1.2-2.8-0.1-3.9c1-1.1,2.8-1.2,3.9-0.1v0
			c1.1,1,1.2,2.8,0.1,3.9c-0.5,0.6-1.2,0.9-2,0.9C476.3,92.2,475.6,91.9,475.1,91.5 M114.3,79.3c-1-1.2-0.8-2.9,0.4-3.9
			c1.2-1,2.9-0.8,3.9,0.4c1,1.2,0.8,2.9-0.4,3.9c-0.5,0.4-1.1,0.6-1.7,0.6C115.7,80.3,114.9,79.9,114.3,79.3 M461.1,79.3
			c-1.2-1-1.3-2.7-0.4-3.9c1-1.2,2.7-1.3,3.9-0.4c1.2,1,1.3,2.7,0.4,3.9C464,80.1,462.3,80.2,461.1,79.3 M129.1,67.8
			c-0.9-1.2-0.6-3,0.6-3.8c1.2-0.9,3-0.6,3.8,0.6c0.9,1.2,0.6,3-0.6,3.8c-0.5,0.3-1,0.5-1.6,0.5C130.5,68.9,129.6,68.5,129.1,67.8
			 M446.4,68.1c-1.2-0.9-1.5-2.6-0.6-3.8c0.9-1.2,2.6-1.5,3.8-0.6c1.2,0.9,1.5,2.6,0.6,3.8c-0.5,0.7-1.4,1.2-2.2,1.2
			C447.4,68.6,446.8,68.4,446.4,68.1 M144.6,57.3c-0.8-1.3-0.4-3,0.9-3.8c1.3-0.8,3-0.4,3.8,0.9s0.4,3-0.9,3.8
			c-0.4,0.3-0.9,0.4-1.4,0.4C146,58.6,145.1,58.1,144.6,57.3 M430.9,57.9c-1.3-0.8-1.7-2.5-0.9-3.8c0.8-1.3,2.5-1.7,3.8-0.9
			s1.7,2.5,0.9,3.8c0,0,0,0,0,0C433.9,58.3,432.2,58.7,430.9,57.9 M160.8,47.9c-0.7-1.3-0.2-3,1.2-3.7c1.3-0.7,3-0.2,3.7,1.2
			c0.7,1.3,0.2,3-1.2,3.7c0,0,0,0,0,0v0c-0.4,0.2-0.8,0.3-1.3,0.3C162.2,49.4,161.3,48.8,160.8,47.9 M414.7,48.8
			c-1.3-0.7-1.9-2.4-1.2-3.7c0.7-1.3,2.4-1.9,3.7-1.2c1.3,0.7,1.9,2.4,1.2,3.7c-0.5,0.9-1.4,1.5-2.4,1.5
			C415.6,49.1,415.1,49,414.7,48.8 M177.6,39.7c-0.6-1.4,0-3,1.4-3.6s3,0,3.6,1.4c0.6,1.4,0,3-1.4,3.6c0,0,0,0,0,0
			c-0.3,0.2-0.7,0.2-1.1,0.2C179,41.3,178,40.7,177.6,39.7 M398,40.8c-1.4-0.6-2.1-2.2-1.5-3.6c0.6-1.4,2.2-2.1,3.6-1.5
			c1.4,0.6,2.1,2.2,1.5,3.6c-0.4,1-1.4,1.7-2.5,1.7C398.7,41.1,398.3,41,398,40.8 M194.9,32.6c-0.5-1.4,0.2-3,1.7-3.5
			c1.4-0.5,3,0.2,3.5,1.7c0.5,1.4-0.2,3-1.7,3.5c-0.3,0.1-0.6,0.2-0.9,0.2C196.3,34.4,195.2,33.7,194.9,32.6 M380.7,34
			c-1.4-0.5-2.2-2.1-1.7-3.5c0.5-1.4,2.1-2.2,3.5-1.7c1.4,0.5,2.2,2.1,1.7,3.5c-0.4,1.1-1.4,1.9-2.6,1.9
			C381.4,34.2,381,34.1,380.7,34 M212.6,26.7c-0.4-1.5,0.4-3,1.9-3.4c1.5-0.4,3,0.4,3.4,1.9c0.4,1.5-0.4,3-1.9,3.4
			c-0.2,0.1-0.5,0.1-0.7,0.1C214,28.7,213,27.9,212.6,26.7 M363.1,28.4c-1.4-0.4-2.3-1.9-1.8-3.4c0.4-1.4,1.9-2.3,3.4-1.8
			c1.4,0.4,2.3,1.9,1.8,3.4c-0.3,1.2-1.4,2-2.6,1.9C363.6,28.5,363.3,28.5,363.1,28.4 M230.7,22.1c-0.3-1.5,0.6-2.9,2.1-3.2
			c1.5-0.3,2.9,0.6,3.2,2.1c0.3,1.5-0.6,2.9-2.1,3.2c0,0,0,0,0,0c-0.2,0-0.4,0.1-0.6,0.1C232.1,24.3,231,23.4,230.7,22.1
			 M345.1,24.1c-1.5-0.3-2.4-1.8-2.1-3.2c0.3-1.5,1.8-2.4,3.2-2.1c1.5,0.3,2.4,1.8,2.1,3.2c-0.3,1.3-1.4,2.2-2.7,2.2
			C345.5,24.1,345.3,24.1,345.1,24.1 M249.1,18.7c-0.2-1.5,0.8-2.9,2.3-3.1c1.5-0.2,2.9,0.8,3.1,2.3c0.2,1.5-0.8,2.9-2.3,3.1
			c0,0,0,0,0,0c-0.1,0-0.3,0-0.4,0C250.5,21.1,249.3,20,249.1,18.7 M326.8,20.9L326.8,20.9c-1.5-0.2-2.6-1.6-2.4-3.1
			c0.2-1.5,1.6-2.6,3.1-2.4s2.6,1.6,2.4,3.1c-0.2,1.4-1.3,2.4-2.7,2.4C327.1,21,327,20.9,326.8,20.9"/>
                            <path id="arrow_low" className="st3" d="M290.1,573.7L278,554h24.1L290.1,573.7z"/>
                            <path id="arrow_high" className="st3" d="M290.1,3.6l12.1,19.7H278L290.1,3.6z"/>
                        </g>
                    </g>
                    <g>
                        <path className="dots-top" d="M279.8,51.5c-0.1-1.4,0.9-2.6,2.3-2.7l0,0c1.4-0.1,2.6,0.9,2.7,2.3l0,0c0.1,1.4-0.9,2.6-2.3,2.7l0,0
	c-0.1,0-0.1,0-0.2,0l0,0C281,53.8,279.9,52.8,279.8,51.5z M316.9,53.7c-1.4-0.1-2.4-1.3-2.3-2.7l0,0c0.1-1.4,1.3-2.4,2.7-2.3l0,0
	l0,0l0,0c1.4,0.1,2.4,1.3,2.3,2.7l0,0c-0.1,1.3-1.2,2.3-2.5,2.3l0,0C317,53.7,316.9,53.7,316.9,53.7z M296.8,50.7
	c0-1.4,1.1-2.5,2.5-2.5l0,0c0.1,0,0.2,0,0.3,0l0,0c0.1,0,0.2,0,0.3,0l0,0c0,0,0,0,0,0l0,0c1.4,0,2.5,1.1,2.5,2.5l0,0
	c0,1.4-1.1,2.5-2.5,2.5l0,0c-0.1,0-0.2,0-0.3,0l0,0c-0.1,0-0.2,0-0.3,0l0,0C298,53.2,296.8,52.1,296.8,50.7z"/>
                        <path className="dots-bottom" d="M297.8,549.3c0-1.4,1.1-2.5,2.5-2.5l0,0c1.4,0,2.5,1.1,2.5,2.5l0,0c0,1.4-1.1,2.5-2.5,2.5l0,0
	C298.9,551.8,297.8,550.7,297.8,549.3z M283,551.3c-1.4-0.1-2.4-1.3-2.3-2.7l0,0c0.1-1.4,1.3-2.4,2.6-2.3l0,0
	c1.4,0.1,2.4,1.3,2.3,2.7l0,0c-0.1,1.3-1.2,2.3-2.5,2.3l0,0C283.2,551.3,283.1,551.3,283,551.3z M314.8,548.9
	c-0.1-1.4,1-2.6,2.3-2.6v0c1.4-0.1,2.6,1,2.7,2.3l0,0c0.1,1.4-0.9,2.6-2.3,2.7l0,0c0,0,0,0,0,0l0,0c-0.1,0-0.1,0-0.2,0l0,0
	C316,551.3,314.9,550.3,314.8,548.9z"/>
                    </g>
                </svg>
                <svg version="1.1" className="pulsate-wrapper pulsate-low-wrapper" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 100 100">
                    <path className="low-pulse" d="M50.5,58.9l-11-18h22L50.5,58.9z"/>
                    <path className="low-pulse" d="M50.5,58.9l-11-18h22L50.5,58.9z"/>
                    <path className="low-pulse" d="M50.5,58.9l-11-18h22L50.5,58.9z"/>
                </svg>
                <svg version="1.1" className="pulsate-wrapper pulsate-high-wrapper" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 100 100">
                    <path className="high-pulse" d="M50.5,40.9l11,18h-22L50.5,40.9z"/>
                    <path className="high-pulse" d="M50.5,40.9l11,18h-22L50.5,40.9z"/>
                    <path className="high-pulse" d="M50.5,40.9l11,18h-22L50.5,40.9z"/>
                </svg>

            </div>
            <div ref={pathRef} className="path-box">
                <div className="fake-ball"
                     style={fakeBallStyle}
                     onMouseDown={!isTouchDevice? handleBallTouchDown : () => {}}
                     onTouchStart={isTouchDevice? handleBallTouchDown : () => {}} />
            </div>
        </>
    )
}
